// src/calculation/generateTables/generateTables.js
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { buildAndSortRectsByColor, parseTablePositions } from "./dataLoad";
import { processGenerate } from "./processGenerate";
import { resultHandle } from "./resultHandle";
import { updateWork } from "./generateUtils";

export function useGenerateTables() {
  const works = useSelector((s) => s.works);
  const createdItems = useSelector((s) => s.createdItems);
  const createdTables = useSelector((s) => s.createdTables);
  const colors = useSelector((s) => s.colors);

  return useCallback(
    (workId, options = {}, overrideItems = null, overrideTables = null) => {
      const {
        scope = "work",
        mode = "full",
        colorIds = [],
        tableIds = [],
        TRIM = 10,
        direction = "both",
        allowHalfTable = true
      } = options;

      const workID = Number(workId);
      const work = works.find((w) => w.workId === workID);
      if (!work) throw new Error("Work not found: ", workId);

      const sourceItems = overrideItems ?? createdItems;
      const sourceTables = overrideTables ?? createdTables;
      const allItems = sourceItems.filter((it) => it.work?.workId === workID);
      const selectedTableIdSet = new Set(
        tableIds.map((tableId) => String(tableId))
      );

      let filteredItems = allItems;
      if (scope === "colors") {
        filteredItems = allItems.filter((it) =>
          colorIds.includes(it.color?.colorId)
        );
      }
      if (scope === "tables") {
        if (mode === "lines") {
          filteredItems = allItems.filter((item) =>
            parseTablePositions(item.tablePosition).some((position) =>
              selectedTableIdSet.has(String(position[3]))
            )
          );
        } else {
          filteredItems = allItems.filter(
            (item) =>
              typeof item.generateQty === "number" && item.generateQty > 0
          );
        }
      }
      //console.log("filteredItems: ", filteredItems);
      const rectsByColor = buildAndSortRectsByColor(
        filteredItems,
        mode,
        tableIds
      );
      const existingForWork = sourceTables.filter(
        (t) => String(t.work?.workId) === String(workID)
      );
      let filteredTables = existingForWork;
      if (scope === "colors") {
        filteredTables = existingForWork.filter((t) =>
          colorIds.includes(t.color?.colorId)
        );
      }
      if (scope === "tables") {
        filteredTables = existingForWork.filter((table) =>
          selectedTableIdSet.has(String(table.id))
        );
      }
      const context = {
        work,
        rectsByColor,
        existingTables: filteredTables,
        colors,
        createdItems: filteredItems
      };
      if (process.env.NODE_ENV === "development") {
        console.groupCollapsed(
          `🧠 GENERATE INPUT | work=${workID} | scope=${scope}`
        );

        console.log("Options:", {
          scope,
          mode,
          colorIds,
          tableIds,
          TRIM,
          direction,
          allowHalfTable
        });
        console.log("Filtered items:", filteredItems);
        console.log("Rects by color:", rectsByColor);
        console.log("Existing tables:", filteredTables);

        console.groupEnd();
      }
      const genResult = processGenerate({
        ...context,
        mode: mode === "lines" ? "replay" : "optimize",
        TRIM,
        direction,
        allowHalfTable,
        getSheetSize(colorId) {
          const color = colors.find(
            (c) => String(c.colorId) === String(colorId)
          );
          if (!color || !color.dimension) return { sheetW: 0, sheetH: 0 };
          const nums = color.dimension
            .replace("[", "")
            .replace("]", "")
            .split(",")
            .map((n) => Number(n.trim()));
          return {
            sheetH: nums[0],
            sheetW: nums[1]
          };
        }
      });
      if (scope === "tables" && mode === "lines") {
        const generatedTableIdSet = new Set(
          (genResult.createdTables || []).map((table) => String(table.id))
        );

        const missingTableIds = [...selectedTableIdSet].filter(
          (tableId) => !generatedTableIdSet.has(tableId)
        );

        if (missingTableIds.length > 0) {
          throw new Error(
            `A replay nem generálta újra a következő táblát: ` +
              missingTableIds.join(", ")
          );
        }
      }

      const updatedItems = resultHandle({
        createdItems: allItems,
        placedRects: genResult.placedRects,
        createdTables: genResult.createdTables,
        scope,
        tableIds,
        colorIds
      });

      let finalTables = genResult.createdTables;

      if (scope !== "work") {
        const untouched = existingForWork.filter((table) => {
          if (scope === "colors") {
            return !colorIds.some(
              (colorId) => String(colorId) === String(table.color?.colorId)
            );
          }

          if (scope === "tables") {
            return !selectedTableIdSet.has(String(table.id));
          }

          return true;
        });

        finalTables = [...untouched, ...genResult.createdTables];
      }

      const updatedWork = updateWork(work, finalTables, colors);
      const result = {
        createdItems: updatedItems.map((item) => ({
          ...item,
          ...(mode !== "lines" && { frontend: true })
        })),
        createdTables: finalTables.map((table) => ({
          ...table,
          frontend: true
        })),
        work: updatedWork,
        meta: genResult.meta
      };
      return result;
    },
    [works, createdItems, createdTables, colors]
  );
}
